import {
	CSSProperties,
	FC,
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import {
	Button,
	DatePicker,
	Empty,
	Input,
	message,
	Modal,
	Popover,
	Select,
	Spin,
	Tag,
	Tooltip
} from 'antd'
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	FileTextOutlined,
	LeftOutlined,
	PrinterOutlined,
	RightOutlined,
	ThunderboltOutlined,
	ToolOutlined
} from '@ant-design/icons'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	downloadJournalReport,
	updateRecordStatus,
	JournalRecordItem,
	JournalRecordsResponse
} from '../api/relay'
import { relayStore } from '../store/relay'
import {
	ElectricMechanic,
	ElectricMechanicValue,
	RecordStatus,
	RelayType
} from '../helpers/types/relay'
import { PageBreadcrumb } from '../components/PageBreadcrumb'
import { RNJournal, RNRelayOneRecords } from '../helpers/routeNames'
import { authStore } from '../store/auth'
import { Dayjs } from 'dayjs'
import '../styles/Journal.scss'

const PAGE_SIZE = 10

type JournalRecord = JournalRecordItem

const STATUS_TAGS: Record<
	string,
	{ color: string; icon: ReactNode; label: string }
> = {
	pending: {
		color: 'default',
		icon: <ClockCircleOutlined />,
		label: 'Ожидание'
	},
	mechanic: {
		color: 'processing',
		icon: <ToolOutlined />,
		label: 'Механика'
	},
	electric: {
		color: 'warning',
		icon: <ThunderboltOutlined />,
		label: 'Электрика'
	},
	approved: {
		color: 'success',
		icon: <CheckCircleOutlined />,
		label: 'Утверждено'
	},
	rejected: {
		color: 'error',
		icon: <CloseCircleOutlined />,
		label: 'Отклонено'
	}
}

// Helpers

function buildValueMap(
	values: ElectricMechanicValue[],
	key: 'mechanicId' | 'electricId'
): Map<string, string> {
	const map = new Map<string, string>()
	for (const v of values) {
		const id = v[key]
		if (id) map.set(id, v.value)
	}
	return map
}

function getValueCols(cols: ElectricMechanic[]): ElectricMechanic[] {
	return cols
		.filter((c) => c.isAcceptsValue)
		.sort((a, b) => a.orderInLevel - b.orderInLevel)
}

function getMaxLevel(cols: ElectricMechanic[]): number {
	return cols.reduce((m, c) => Math.max(m, c.level), 0)
}

function normalizeJournalResponse(response: JournalRecordsResponse): {
	records: JournalRecord[]
	totalPages: number | null
} {
	return {
		records: Array.isArray(response.data) ? response.data : [],
		totalPages: Number.isFinite(response.total)
			? Math.max(1, Math.ceil(response.total / PAGE_SIZE))
			: null
	}
}

// ── Print tree helpers ────────────────────────────────────────────────────────
type PrintTreeNode = ElectricMechanic & { children: PrintTreeNode[] }

function buildTree(items: ElectricMechanic[]): PrintTreeNode[] {
	const nodeMap = new Map<string, PrintTreeNode>()
	for (const item of items) {
		nodeMap.set(item.id, { ...item, children: [] })
	}
	const roots: PrintTreeNode[] = []
	for (const node of Array.from(nodeMap.values())) {
		if (node.parentId && nodeMap.has(node.parentId)) {
			nodeMap.get(node.parentId)!.children.push(node)
		} else {
			roots.push(node)
		}
	}
	const sortByOrder = (nodes: PrintTreeNode[]): PrintTreeNode[] =>
		[...nodes]
			.sort((a, b) => a.orderInLevel - b.orderInLevel)
			.map((n) => ({ ...n, children: sortByOrder(n.children) }))
	return sortByOrder(roots)
}

export const Journal: FC = () => {
	const { relayTypeId } = useParams<{ relayTypeId: string }>()
	const { relayTypes, getRecordsJournal } = relayStore()
	const { user } = authStore()
	const navigate = useNavigate()
	const location = useLocation()

	const canDownloadPdf =
		user?.role === 'admin' || user?.role === 'supervisor'

	const [page, setPage] = useState<number>(() =>
		parseInt(
			new URLSearchParams(location.search).get('page') || '1',
			10
		)
	)
	const [records, setRecords] = useState<JournalRecord[]>([])
	const [loading, setLoading] = useState(true)
	const [totalPages, setTotalPages] = useState<number | null>(null)

	// PDF download modal state
	const [pdfModalOpen, setPdfModalOpen] = useState(false)
	const [pdfRelayId, setPdfRelayId] = useState<string | undefined>()
	const [pdfDates, setPdfDates] = useState<
		[Dayjs | null, Dayjs | null]
	>([null, null])
	const [pdfLoading, setPdfLoading] = useState(false)

	const handleDownloadPdf = async () => {
		if (!pdfRelayId || !pdfDates[0] || !pdfDates[1]) return
		setPdfLoading(true)
		try {
			await downloadJournalReport({
				relayId: pdfRelayId,
				startDate: pdfDates[0].format('YYYY-MM-DD'),
				endDate: pdfDates[1].format('YYYY-MM-DD')
			})
			setPdfModalOpen(false)
		} catch {
			message.error('Не удалось сформировать отчёт')
		} finally {
			setPdfLoading(false)
		}
	}

	const relayType = useMemo(
		() => relayTypes.find((rt) => rt.id === relayTypeId),
		[relayTypes, relayTypeId]
	)

	const mechValueCols = useMemo(
		() => getValueCols(relayType?.mechanics ?? []),
		[relayType]
	)
	const elecValueCols = useMemo(
		() => getValueCols(relayType?.electrics ?? []),
		[relayType]
	)
	const mechMaxLevel = useMemo(
		() => getMaxLevel(relayType?.mechanics ?? []),
		[relayType]
	)
	const elecMaxLevel = useMemo(
		() => getMaxLevel(relayType?.electrics ?? []),
		[relayType]
	)

	const totalCols =
		3 + mechValueCols.length + elecValueCols.length + 2

	useEffect(() => {
		setLoading(true)
		getRecordsJournal(page, relayTypeId)
			.then((response) => {
				const normalized = normalizeJournalResponse(response)
				setRecords(normalized.records)
				setTotalPages(normalized.totalPages)
			})
			.finally(() => setLoading(false))
	}, [getRecordsJournal, page, relayTypeId])

	const isLastPage =
		totalPages !== null
			? page >= totalPages
			: records.length < PAGE_SIZE

	useEffect(() => {
		const params = new URLSearchParams(location.search)
		params.set('page', String(page))
		navigate({ search: params.toString() }, { replace: true })
	}, [page, navigate, location.search])

	if (!relayTypes.length) {
		return (
			<div className="journal-page-loading">
				<Spin size="large" />
			</div>
		)
	}

	if (!relayType) {
		return (
			<Empty
				description="Тип реле не найден"
				style={{ padding: 80 }}
			/>
		)
	}

	return (
		<div className="journal-page">
			<PageBreadcrumb
				items={[
					{ label: 'Журнал', path: RNJournal },
					{ label: relayType.name }
				]}
			/>

			{/* Top info bar */}
			<div className="journal-topbar">
				<div className="journal-topbar-title">
					<h1>{relayType.name}</h1>
					<span className="journal-topbar-sub">
						Журнал характеристик - страница {page}
					</span>
				</div>
				<div className="journal-topbar-actions">
					<div className="journal-topbar-stat">
						<span className="stat-dot stat-mech" />
						Механических: <b>{mechValueCols.length}</b>
					</div>
					<div className="journal-topbar-stat">
						<span className="stat-dot stat-elec" />
						Электрических: <b>{elecValueCols.length}</b>
					</div>
					<Button
						icon={<PrinterOutlined />}
						onClick={() => window.print()}
						size="small">
						Печать
					</Button>
					{canDownloadPdf && (
						<Button
							icon={<FileTextOutlined />}
							onClick={() => setPdfModalOpen(true)}
							size="small">
							PDF отчёт
						</Button>
					)}
				</div>
			</div>

			{/* PDF Download Modal */}
			{canDownloadPdf && (
				<Modal
					title="Скачать PDF отчёт"
					open={pdfModalOpen}
					onCancel={() => setPdfModalOpen(false)}
					onOk={handleDownloadPdf}
					okText="Скачать"
					cancelText="Отмена"
					okButtonProps={{
						disabled:
							!pdfRelayId ||
							!pdfDates[0] ||
							!pdfDates[1],
						loading: pdfLoading
					}}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 12
						}}>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: 4,
									fontWeight: 500
								}}>
								Реле
							</label>
							<Select
								style={{ width: '100%' }}
								placeholder="Выберите реле"
								value={pdfRelayId}
								onChange={setPdfRelayId}
								options={(
									relayType?.relays ?? []
								).map((r) => ({
									value: r.id,
									label: r.name
								}))}
							/>
						</div>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: 4,
									fontWeight: 500
								}}>
								Период
							</label>
							<DatePicker.RangePicker
								style={{ width: '100%' }}
								value={pdfDates}
								onChange={(dates) =>
									setPdfDates(
										dates as [
											Dayjs | null,
											Dayjs | null
										]
									)
								}
							/>
						</div>
					</div>
				</Modal>
			)}

			{/* Table area */}
			{loading ? (
				<div className="journal-center">
					<Spin size="large" />
				</div>
			) : records.length === 0 && page === 1 ? (
				<div className="journal-empty">
					<FileTextOutlined style={{ fontSize: 48 }} />
					<p>Записей пока нет</p>
				</div>
			) : (
				<>
					<div className="journal-table-wrapper">
						<table className="journal-table">
							<CombinedHeader
								mechanics={relayType.mechanics}
								electrics={relayType.electrics}
								mechValueCols={mechValueCols}
								elecValueCols={elecValueCols}
								mechMaxLevel={mechMaxLevel}
								elecMaxLevel={elecMaxLevel}
							/>
							<tbody>
								{/* Column numbering row */}
								<tr className="col-number-row">
									<th>1</th>
									<th>2</th>
									<th>3</th>
									{mechValueCols.map((_, i) => (
										<th key={`mn${i}`}>
											{i + 4}
										</th>
									))}
									{elecValueCols.map((_, i) => (
										<th key={`en${i}`}>
											{mechValueCols.length +
												i +
												4}
										</th>
									))}
									<th>
										{mechValueCols.length +
											elecValueCols.length +
											4}
									</th>
									<th>
										{mechValueCols.length +
											elecValueCols.length +
											5}
									</th>
								</tr>

								{records.map((record, idx) => (
									<CombinedRow
										key={record.id ?? idx}
										record={record}
										mechValueCols={mechValueCols}
										elecValueCols={elecValueCols}
										relayTypeName={relayType.name}
										rowIndex={
											(page - 1) * PAGE_SIZE +
											idx +
											1
										}
									/>
								))}

								{/* Filler rows */}
								{records.length < PAGE_SIZE &&
									Array.from({
										length:
											PAGE_SIZE - records.length
									}).map((_, i) => (
										<tr
											key={`fill-${i}`}
											className="fill-row">
											{Array.from({
												length: totalCols
											}).map((_, j) => (
												<td key={j} />
											))}
										</tr>
									))}
							</tbody>
						</table>
					</div>

					<div className="journal-pagination">
						<Button
							icon={<LeftOutlined />}
							onClick={() => setPage((p) => p - 1)}
							disabled={page === 1}
							size="small">
							Назад
						</Button>
						<span className="page-info">
							Страница {page}
						</span>
						<Button
							icon={<RightOutlined />}
							iconPosition="end"
							onClick={() => setPage((p) => p + 1)}
							disabled={isLastPage}
							size="small">
							Вперед
						</Button>
					</div>
				</>
			)}

			{/* Custom print report — hidden on screen, shown on print */}
			{relayType && (
				<JournalPrintView
					relayType={relayType}
					records={records}
					page={page}
				/>
			)}
		</div>
	)
}

// ── JournalPrintView ─────────────────────────────────────────────────────────

const PrintNodeRenderer: FC<{
	node: PrintTreeNode
	valueMap: Map<string, string>
	depth: number
}> = ({ node, valueMap, depth }) => {
	const indent = depth * 16
	const value = node.isAcceptsValue
		? (valueMap.get(node.id) ?? '—')
		: null

	return (
		<>
			<div
				className="print-tree-node"
				style={{ paddingLeft: indent }}>
				<span className="print-node-name">{node.name}</span>
				{node.measurement && (
					<span className="print-node-unit">
						&nbsp;({node.measurement})
					</span>
				)}
				{node.isAcceptsValue ? (
					<span className="print-node-value">
						: {value}
					</span>
				) : (
					<span className="print-node-colon">:</span>
				)}
			</div>
			{node.children.map((child) => (
				<PrintNodeRenderer
					key={child.id}
					node={child}
					valueMap={valueMap}
					depth={depth + 1}
				/>
			))}
		</>
	)
}

const JournalPrintView: FC<{
	relayType: RelayType
	records: JournalRecord[]
	page: number
}> = ({ relayType, records, page }) => {
	const mechTree = useMemo(
		() => buildTree(relayType.mechanics),
		[relayType.mechanics]
	)
	const elecTree = useMemo(
		() => buildTree(relayType.electrics),
		[relayType.electrics]
	)

	const printDate = new Date().toLocaleDateString('ru', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})

	return (
		<div className="journal-print-view">
			{/* Report header */}
			<div className="print-report-header">
				<div className="print-report-title">
					{relayType.name}
				</div>
				<div className="print-report-sub">
					Журнал характеристик — страница {page}
				</div>
				<div className="print-report-meta">
					Дата печати: {printDate}
				</div>
			</div>

			{records.map((record, idx) => {
				const mechMap = buildValueMap(
					record.mechanicValues,
					'mechanicId'
				)
				const elecMap = buildValueMap(
					record.electricValues,
					'electricId'
				)
				const dateStr = record.createdAt
					? new Date(record.createdAt).toLocaleDateString(
							'ru'
						)
					: '—'
				const statusLabel =
					STATUS_TAGS[record.status ?? 'pending']?.label ??
					record.status

				return (
					<div
						key={record.id ?? idx}
						className="print-record-block">
						{/* Record summary line */}
						<div className="print-record-summary">
							<span className="print-record-index">
								#{(page - 1) * PAGE_SIZE + idx + 1}
							</span>
							<span className="print-record-relay">
								{record.relay?.name ?? '—'}
							</span>
							<span className="print-record-year">
								{record.relayOne?.madeYear ?? '—'} г.
							</span>
							<span className="print-record-date">
								{dateStr}
							</span>
							<span className="print-record-status">
								{statusLabel}
							</span>
						</div>

						<div className="print-record-sections">
							{mechTree.length > 0 && (
								<div className="print-section">
									<div className="print-section-title print-section-mech">
										Механические характеристики
									</div>
									{mechTree.map((node) => (
										<PrintNodeRenderer
											key={node.id}
											node={node}
											valueMap={mechMap}
											depth={0}
										/>
									))}
								</div>
							)}

							{elecTree.length > 0 && (
								<div className="print-section">
									<div className="print-section-title print-section-elec">
										Электрические характеристики
									</div>
									{elecTree.map((node) => (
										<PrintNodeRenderer
											key={node.id}
											node={node}
											valueMap={elecMap}
											depth={0}
										/>
									))}
								</div>
							)}
						</div>
					</div>
				)
			})}
		</div>
	)
}

const CombinedHeader: FC<{
	mechanics: ElectricMechanic[]
	electrics: ElectricMechanic[]
	mechValueCols: ElectricMechanic[]
	elecValueCols: ElectricMechanic[]
	mechMaxLevel: number
	elecMaxLevel: number
}> = ({
	mechanics,
	electrics,
	mechValueCols,
	elecValueCols,
	mechMaxLevel,
	elecMaxLevel
}) => {
	const totalLevelRows = Math.max(mechMaxLevel, elecMaxLevel, 1)
	const fixedRowSpan = totalLevelRows + 1

	const renderHeaderCell = (
		col: ElectricMechanic,
		prefix: 'm-' | 'e-'
	) => (
		<th
			key={prefix + col.id}
			colSpan={col.colspan}
			rowSpan={col.rowspan}
			className={
				prefix === 'm-' ? 'header-mech' : 'header-elec'
			}>
			{col.vertical ? (
				<span className="vertical-text">
					{col.name}
					{col.measurement && (
						<em className="measurement-label">
							({col.measurement})
						</em>
					)}
				</span>
			) : (
				<>
					{col.name}
					{col.measurement && (
						<em className="measurement-label">
							{col.measurement}
						</em>
					)}
				</>
			)}
		</th>
	)

	const levelRows = useMemo(() => {
		const result: ReactNode[][] = []
		for (let level = 1; level <= totalLevelRows; level++) {
			const mechCells = mechanics
				.filter((c) => c.level === level)
				.sort((a, b) => a.orderInLevel - b.orderInLevel)
				.map((col) => renderHeaderCell(col, 'm-'))

			const elecCells = electrics
				.filter((c) => c.level === level)
				.sort((a, b) => a.orderInLevel - b.orderInLevel)
				.map((col) => renderHeaderCell(col, 'e-'))

			result.push([...mechCells, ...elecCells])
		}
		return result
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mechanics, electrics, totalLevelRows])

	// Every header cell is position:sticky. The header is 2-5 rows tall depending
	// on the data, and the rows have different heights (vertical labels), so the
	// offsets have to be measured — with a single top:0 the level rows pin onto
	// the super-row and the header stacks on top of itself while scrolling.
	const theadRef = useRef<HTMLTableSectionElement>(null)
	const [rowTops, setRowTops] = useState<number[]>([])

	useLayoutEffect(() => {
		const thead = theadRef.current
		if (!thead) return

		const measure = () => {
			let offset = 0
			const tops = Array.from(thead.rows).map((row) => {
				const top = offset
				offset += row.getBoundingClientRect().height
				return top
			})
			setRowTops((prev) =>
				prev.length === tops.length &&
				prev.every((v, i) => Math.abs(v - tops[i]) < 0.5)
					? prev
					: tops
			)
		}

		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(thead)
		return () => observer.disconnect()
	}, [mechanics, electrics, totalLevelRows])

	const stickyTop = (rowIndex: number) =>
		({
			'--row-top': `${rowTops[rowIndex] ?? 0}px`
		}) as CSSProperties

	return (
		<thead ref={theadRef}>
			{/* Group super-row */}
			<tr style={stickyTop(0)}>
				<th rowSpan={fixedRowSpan} className="header-fixed">
					<span className="vertical-text">№</span>
				</th>
				<th rowSpan={fixedRowSpan} className="header-fixed">
					<span className="vertical-text">Тип реле</span>
				</th>
				<th rowSpan={fixedRowSpan} className="header-fixed">
					<span className="vertical-text">
						Номер реле / Год выпуска
					</span>
				</th>
				{mechValueCols.length > 0 && (
					<th
						colSpan={mechValueCols.length}
						className="header-section-mech">
						Механические характеристики
					</th>
				)}
				{elecValueCols.length > 0 && (
					<th
						colSpan={elecValueCols.length}
						className="header-section-elec">
						Электрические характеристики
					</th>
				)}
				<th rowSpan={fixedRowSpan} className="header-fixed">
					<span className="vertical-text">
						Дата проверки
					</span>
				</th>
				<th rowSpan={fixedRowSpan} className="header-fixed">
					<span className="vertical-text">Статус</span>
				</th>
			</tr>

			{/* Dynamic level rows */}
			{levelRows.map((cells, i) => (
				<tr key={`lvl-${i}`} style={stickyTop(i + 1)}>
					{cells}
				</tr>
			))}
		</thead>
	)
}

// Combined data row

const CombinedRow: FC<{
	record: JournalRecord
	mechValueCols: ElectricMechanic[]
	elecValueCols: ElectricMechanic[]
	relayTypeName: string
	rowIndex: number
}> = ({
	record,
	mechValueCols,
	elecValueCols,
	relayTypeName,
	rowIndex
}) => {
	const { user } = authStore()
	const navigate = useNavigate()
	const canApprove =
		user?.role === 'admin' || user?.role === 'supervisor'

	const mechMap = useMemo(
		() => buildValueMap(record.mechanicValues, 'mechanicId'),
		[record.mechanicValues]
	)
	const elecMap = useMemo(
		() => buildValueMap(record.electricValues, 'electricId'),
		[record.electricValues]
	)

	const [localStatus, setLocalStatus] = useState<RecordStatus>(
		(record.status as RecordStatus) ?? 'pending'
	)
	const [rejectionReason, setRejectionReason] = useState('')
	const [rejectOpen, setRejectOpen] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const handleApprove = useCallback(async () => {
		if (!record.id) return
		setActionLoading(true)
		try {
			await updateRecordStatus(record.id, {
				status: 'approved'
			})
			setLocalStatus('approved')
			message.success('Запись утверждена')
		} catch {
			message.error('Не удалось утвердить запись')
		} finally {
			setActionLoading(false)
		}
	}, [record.id])

	const handleReject = useCallback(async () => {
		if (!record.id || !rejectionReason.trim()) return
		setActionLoading(true)
		try {
			await updateRecordStatus(record.id, {
				status: 'rejected',
				rejectionReason: rejectionReason.trim()
			})
			setLocalStatus('rejected')
			setRejectOpen(false)
			setRejectionReason('')
			message.success('Запись отклонена')
		} catch {
			message.error('Не удалось отклонить запись')
		} finally {
			setActionLoading(false)
		}
	}, [record.id, rejectionReason])

	const statusTag = STATUS_TAGS[localStatus] ?? STATUS_TAGS.pending

	const dateStr = record.createdAt
		? new Date(record.createdAt).toLocaleDateString('ru')
		: '—'
	const fullDateStr = record.createdAt
		? new Date(record.createdAt).toLocaleString('ru')
		: undefined

	const canNavigate =
		record.id &&
		record.relayTypeId &&
		record.relayId &&
		record.relayOne?.id

	const handleRowClick = () => {
		if (!canNavigate) return
		navigate(
			`${RNRelayOneRecords}/${record.relayTypeId}/${record.relayId}/${record.relayOne!.id}/${record.id}`
		)
	}

	return (
		<tr
			style={canNavigate ? { cursor: 'pointer' } : undefined}
			onClick={handleRowClick}>
			<td className="cell-meta cell-index">{rowIndex}</td>
			<td className="cell-meta">{relayTypeName}</td>
			<td className="cell-meta cell-relay">
				<span className="relay-name">
					{record.relay?.name ?? '—'}
				</span>
				<span className="relay-year">
					{record.relayOne?.madeYear ?? '—'} г.
				</span>
			</td>

			{mechValueCols.map((col) => {
				const val = mechMap.get(col.id)
				return (
					<td
						key={'mv-' + col.id}
						className={
							'cell-mech' + (val ? '' : ' cell-empty')
						}>
						{val ?? '—'}
					</td>
				)
			})}

			{elecValueCols.map((col) => {
				const val = elecMap.get(col.id)
				return (
					<td
						key={'ev-' + col.id}
						className={
							'cell-elec' + (val ? '' : ' cell-empty')
						}>
						{val ?? '—'}
					</td>
				)
			})}

			<td className="cell-meta cell-date">
				<Tooltip title={fullDateStr}>{dateStr}</Tooltip>
			</td>
			<td
				className="cell-status"
				onClick={(e) => e.stopPropagation()}>
				{canApprove && localStatus === 'electric' ? (
					<div
						style={{
							display: 'flex',
							gap: 4,
							flexWrap: 'wrap'
						}}>
						<Button
							size="small"
							type="primary"
							icon={<CheckCircleOutlined />}
							loading={actionLoading}
							onClick={handleApprove}>
							Утв.
						</Button>
						<Popover
							open={rejectOpen}
							onOpenChange={(v) => {
								if (!actionLoading) setRejectOpen(v)
							}}
							trigger="click"
							placement="topRight"
							content={
								<div style={{ width: 220 }}>
									<Input.TextArea
										rows={3}
										placeholder="Причина отклонения..."
										value={rejectionReason}
										onChange={(e) =>
											setRejectionReason(
												e.target.value
											)
										}
										style={{ marginBottom: 8 }}
									/>
									<Button
										danger
										size="small"
										block
										loading={actionLoading}
										disabled={
											!rejectionReason.trim()
										}
										onClick={handleReject}>
										Подтвердить отклонение
									</Button>
								</div>
							}>
							<Button
								size="small"
								danger
								icon={<CloseCircleOutlined />}>
								Отк.
							</Button>
						</Popover>
					</div>
				) : (
					<Tag
						icon={statusTag.icon}
						color={statusTag.color}
						style={{ margin: 0 }}>
						{statusTag.label}
					</Tag>
				)}
			</td>
		</tr>
	)
}
